import { useCallback, useEffect, useState } from "react";
import {
  VISITED_JOBS_STORAGE_KEY,
  isJobVisitedRecently,
  persistVisitedJobsStore,
  pruneVisitedJobsStore,
  readVisitedJobsStore,
  type VisitedJobsStore,
} from "../lib/visitedJobs";

function storesEqual(a: VisitedJobsStore, b: VisitedJobsStore): boolean {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

export function useVisitedJobs() {
  const [store, setStore] = useState<VisitedJobsStore>(() =>
    typeof window !== "undefined" ? readVisitedJobsStore() : {},
  );

  const refreshFromStorage = useCallback(() => {
    setStore(readVisitedJobsStore());
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === VISITED_JOBS_STORAGE_KEY || e.key === null) {
        refreshFromStorage();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshFromStorage]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const next = readVisitedJobsStore();
      setStore((prev) => (storesEqual(prev, next) ? prev : next));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const markVisited = useCallback((jobId: string) => {
    setStore((prev) => {
      const pruned = pruneVisitedJobsStore(prev);
      const next = { ...pruned, [jobId]: new Date().toISOString() };
      persistVisitedJobsStore(next);
      return next;
    });
  }, []);

  const isVisited = useCallback(
    (jobId: string) => isJobVisitedRecently(store, jobId),
    [store],
  );

  return { markVisited, isVisited };
}
